import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ChevronDown from "../../components/Atomos/Icons/ChevronDown";
import ChevronRight from "../../components/Atomos/Icons/ChevronRight";
import Email from "../../components/Atomos/Icons/Email";
import ProfilePhoto from "../../components/Atomos/ProfilePhoto";
import SocialLinks from "../../components/ViewMode/SocialLinks";
import { getAddresById } from "../../Services/Address";
import { getAOPsByFirm, getAOPsByOffice } from "../../Services/AreaOfPractice";
import { getBlogs } from "../../Services/Blog";
import { getLastContents } from "../../Services/Content";
import {
  getFirmById,
  getFirmByShortId,
  getOfficesByFirmId,
} from "../../Services/Firm";
import { getOfficeContactByFirmId } from "../../Services/MemberMainContact";
import { getOfficeByIdOrLegacyId } from "../../Services/Office";
import { fetchPersonByFirm } from "../../Services/Person";
import { getRepresentativeClientsByFirm } from "../../Services/RepresentativeClient";
import watermark from "../../assets/images&icons/watermark-5.png";
import type { IBlogCard } from "../MyWSG/Cards/Blog";
import type { IAreaOfPractice } from "../../interfaces/models/IAreaOfPractice";
import type { IAddress } from "../../interfaces/models/IAddress";
import type { IContent } from "../../interfaces/models/IContent";
import type IFirm from "../../interfaces/models/IFirm";
import type { IOfficeNew } from "../../interfaces/models/IOffice";
import type { IPerson } from "../../interfaces/models/IPerson";
import type { IRepresentativeClient } from "../../interfaces/models/IRepresentativeClient";
import { formatLocationText, formatTextForUrl } from "../../utils/functions";
import { formatPhoneForDisplay } from "../../utils/formatPhoneForDisplay";
import { resolveProfilePictureDisplayUrl } from "../../utils/profilePictureUrl";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";

/** Office shape from API; optional fields are legacy fallbacks. */
type OfficeRecord = IOfficeNew & {
  addressName?: string;
  name?: string;
};
type ContentRow = IContent & { legacyId?: string | number; shortId?: string };
type RepresentativeClientRecord = IRepresentativeClient & { office?: string | string[] };
type SectionKey =
  | "profile"
  | "aop"
  | "clients"
  | "professionals"
  | "articles"
  | "press"
  | "blogs"
  | "guides";

type LinkTarget = {
  href: string;
  external?: boolean;
};

type OfficeContactGroup = {
  office: string;
  contacts: Array<{ contact: string }>;
};

const SECTION_NAV_ITEMS: Array<{ key: SectionKey; label: string }> = [
  { key: "profile", label: "Overview" },
  { key: "aop", label: "Areas of Practice" },
  { key: "clients", label: "Representative Clients" },
  { key: "professionals", label: "Professionals" },
  { key: "articles", label: "Articles" },
  { key: "press", label: "Press" },
  { key: "blogs", label: "Blogs" },
  { key: "guides", label: "Guides" },
];

const DEFAULT_OPEN_SECTIONS: Record<SectionKey, boolean> = {
  profile: true,
  aop: true,
  clients: true,
  professionals: true,
  articles: true,
  press: true,
  blogs: true,
  guides: true,
};

const SECTION_SCROLL_DELAY_MS = 40;
const STATE_LEVEL_COUNTRIES = new Set([
  "United States of America",
  "USA",
  "Canada",
]);

const normalizeSearchValue = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "");

const isUuidValue = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value
  );

const formatFirmProfileSlug = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatFirmTypeSlug = (serviceType?: string): string => {
  const normalized = (serviceType ?? "").trim().toLowerCase();

  if (normalized === "legal" || normalized === "legal services") {
    return "law-firm";
  }

  if (normalized === "accounting") {
    return "accounting-firm";
  }

  return formatFirmProfileSlug(serviceType ?? "firm").toLowerCase() || "firm";
};

const resolveFirmPublicId = (firm?: Partial<IFirm> | null): string =>
  String(firm?.shortId ?? firm?.legacyId ?? firm?.id ?? "").trim();

const buildFirmProfilePath = (firm: Partial<IFirm>): string => {
  const firmName = firm.name || firm.firmName || "Firm";
  const firmPublicId = resolveFirmPublicId(firm);

  return `/member2/${formatFirmTypeSlug(firm.serviceType)}/${formatFirmProfileSlug(
    firmName
  )}/${firmPublicId}`;
};

const withProtocol = (value?: string): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return "";
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const isMainOffice = (office: Partial<OfficeRecord>): boolean =>
  String(office.officeType ?? "").toLowerCase() === "main";

const formatOfficeTabLabel = (office?: Partial<OfficeRecord> | null): string => {
  if (!office) {
    return "";
  }

  const jurisdiction = getJurisdictionLabel(office);
  if (jurisdiction) {
    return jurisdiction;
  }

  const city = office.cityName?.trim() || office.city?.trim() || "";
  const state = office.stateName?.trim() || office.state?.trim() || "";
  const country = office.countryName?.trim() || office.country?.trim() || "";

  if (STATE_LEVEL_COUNTRIES.has(country)) {
    return [city, state].filter(Boolean).join(", ");
  }

  return [city, country].filter(Boolean).join(", ");
};

const sortOffices = (offices: OfficeRecord[]): OfficeRecord[] =>
  [...offices].sort((left, right) => {
    if (isMainOffice(left) && !isMainOffice(right)) {
      return -1;
    }
    if (!isMainOffice(left) && isMainOffice(right)) {
      return 1;
    }
    return formatOfficeTabLabel(left).localeCompare(formatOfficeTabLabel(right));
  });

const splitTextareaValues = (value?: string): string[] =>
  Array.from(
    new Set(
      (value ?? "")
        .split(/[\r\n]+/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );

const matchesRepresentativeClientOffice = (
  client: RepresentativeClientRecord,
  officeId?: string
): boolean => {
  if (!officeId) {
    return false;
  }

  const offices = Array.isArray(client.office)
    ? client.office
    : typeof client.office === "string"
      ? [client.office]
      : [];

  return offices.includes(officeId);
};

const formatMonthYear = (value: string | Date | undefined): string => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const extractPlainText = (value: unknown): string => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, " ")
      .trim();
  }

  if (Array.isArray(value)) {
    return value.map(extractPlainText).join(" ").replace(/\s+/g, " ").trim();
  }

  if (typeof value === "object") {
    const objectValue = value as Record<string, unknown>;
    if (typeof objectValue.text === "string") {
      return extractPlainText(objectValue.text);
    }
    if (objectValue.children) {
      return extractPlainText(objectValue.children);
    }
    if (objectValue.content) {
      return extractPlainText(objectValue.content);
    }
  }

  return "";
};

const truncateText = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }

  const truncated = value.slice(0, maxLength).trimEnd();
  const lastSpace = truncated.lastIndexOf(" ");

  return `${truncated.slice(0, lastSpace > 0 ? lastSpace : maxLength)}...`;
};

const resolveAopDisplayName = (aop: IAreaOfPractice): string =>
  aop.displayName?.trim() || aop.aliasName?.[0]?.trim() || "";

const resolveLegacyContentId = (item: {
  legacyId?: string | number;
  shortId?: string;
}): string => {
  const rawValue = item.legacyId ?? item.shortId ?? "";
  return String(rawValue).replace(/^P/i, "").trim();
};

const resolveContentTarget = (
  item: Partial<ContentRow>,
  type: "article" | "press"
): LinkTarget | undefined => {
  const rawLink = item.link?.trim();

  if (rawLink) {
    if (/^https?:\/\//i.test(rawLink)) {
      return { href: rawLink, external: true };
    }

    if (rawLink.startsWith("/")) {
      return { href: rawLink };
    }
  }

  const legacyId = resolveLegacyContentId(item);
  if (!legacyId) {
    return undefined;
  }

  return {
    href:
      type === "article"
        ? `/publications.asp?action=article&artid=${encodeURIComponent(legacyId)}`
        : `/releases.asp?action=press&pressid=${encodeURIComponent(legacyId)}`,
  };
};

const resolveGuideTarget = (item: Partial<ContentRow>): LinkTarget | undefined => {
  const rawLink = item.link?.trim();

  if (!rawLink) {
    return undefined;
  }

  if (/^https?:\/\//i.test(rawLink)) {
    return { href: rawLink, external: true };
  }

  if (rawLink.startsWith("/")) {
    return { href: rawLink };
  }

  if (/^www\./i.test(rawLink)) {
    return { href: `https://${rawLink}`, external: true };
  }

  return { href: rawLink };
};

const resolveBlogTarget = (blog: IBlogCard): LinkTarget => {
  if (blog.slug?.trim()) {
    return { href: `/blog/post/${blog.slug}.aspx` };
  }

  return { href: "/blog/default.aspx" };
};

const buildPersonPath = (person: IPerson): string => {
  const slugBase =
    person.profileURL?.trim() || `${person.name ?? ""}${person.lastName ?? ""}`;
  const slug = formatTextForUrl(slugBase) || "profile";
  const personId = person.legacyId ?? person.generatedId;

  return `/profile/attorney/${slug}/${personId}`;
};

const matchesOffice = (person: IPerson, officeId?: string): boolean => {
  if (!officeId) {
    return false;
  }

  const additionalOffices = Array.isArray(person.additionalOffices)
    ? person.additionalOffices
    : [];

  return person.mainOffice === officeId || additionalOffices.includes(officeId);
};

type FirmListingCategoryKey = "partners" | "associates" | "counsel" | "other";
type ProfessionalGroupKey =
  | "executive"
  | "partners"
  | "associates"
  | "counsel"
  | "staff"
  | "other";

const DEFAULT_FIRM_LISTING_CATEGORIES: Record<FirmListingCategoryKey, string> = {
  partners: "Partners",
  associates: "Associates",
  counsel: "Of Counsel",
  other: "Other",
};

const PROFESSIONAL_GROUP_PRIORITY: Record<ProfessionalGroupKey, number> = {
  executive: 0,
  partners: 1,
  associates: 2,
  counsel: 3,
  staff: 4,
  other: 5,
};

const getFirmListingCategoryLabels = (
  listingCategories?: string[] | null
): Record<FirmListingCategoryKey, string> => {
  const cleaned = Array.isArray(listingCategories)
    ? listingCategories.map((value) => String(value ?? "").trim())
    : [];

  return {
    partners: cleaned[0] || DEFAULT_FIRM_LISTING_CATEGORIES.partners,
    associates: cleaned[1] || DEFAULT_FIRM_LISTING_CATEGORIES.associates,
    counsel: cleaned[2] || DEFAULT_FIRM_LISTING_CATEGORIES.counsel,
    other: cleaned[3] || DEFAULT_FIRM_LISTING_CATEGORIES.other,
  };
};

const getProfessionalRawTitle = (person: IPerson): string =>
  (
    [
      person.listTitle,
      person.title,
      person.unlistedTitle,
    ].find((value) => typeof value === "string" && value.trim()) ?? ""
  )
    .toString()
    .trim();

const getProfessionalGroupKey = (
  rawTitle: string,
  listingCategories?: string[] | null
): ProfessionalGroupKey => {
  const normalized = normalizeSearchValue(rawTitle);
  const labels = getFirmListingCategoryLabels(listingCategories);
  const normalizedLabels = {
    partners: normalizeSearchValue(labels.partners),
    associates: normalizeSearchValue(labels.associates),
    counsel: normalizeSearchValue(labels.counsel),
    other: normalizeSearchValue(labels.other),
  };

  if (normalized === normalizedLabels.partners) {
    return "partners";
  }
  if (normalized === normalizedLabels.associates) {
    return "associates";
  }
  if (normalized === normalizedLabels.counsel) {
    return "counsel";
  }
  if (normalized === normalizedLabels.other) {
    return "other";
  }

  if (!normalized || normalized === "other" || normalized === "others") {
    return "other";
  }

  if (normalized.includes("executive")) {
    return "executive";
  }
  if (normalized.includes("staff")) {
    return "staff";
  }

  if (normalized.includes("partner")) {
    return "partners";
  }

  if (normalized.includes("associate")) {
    return "associates";
  }

  if (normalized.includes("counsel")) {
    return "counsel";
  }

  return "other";
};

const getProfessionalGroupLabel = (
  groupKey: ProfessionalGroupKey,
  listingCategories?: string[] | null
): string => {
  const labels = getFirmListingCategoryLabels(listingCategories);;
  if (groupKey === "partners") {
    return labels.partners;
  }
  if (groupKey === "associates") {
    return labels.associates;
  }
  if (groupKey === "counsel") {
    return labels.counsel;
  }
  if (groupKey === "other") {
    return labels.other;
  }
  if (groupKey === "executive") {
    return "Executive";
  }

  return "Staff";
};

const isGenericProfessionalCategoryTitle = (
  rawTitle: string,
  groupKey: ProfessionalGroupKey,
  listingCategories?: string[] | null
): boolean => {
  const normalized = normalizeSearchValue(rawTitle);
  const labels = getFirmListingCategoryLabels(listingCategories);

  if (groupKey === "partners") {
    return new Set([
      "partner",
      "partners",
      normalizeSearchValue(labels.partners),
    ]).has(normalized);
  }

  if (groupKey === "associates") {
    return new Set([
      "associate",
      "associates",
      normalizeSearchValue(labels.associates),
    ]).has(normalized);
  }

  if (groupKey === "counsel") {
    return new Set([
      "counsel",
      "ofcounsel",
      normalizeSearchValue(labels.counsel),
    ]).has(normalized);
  }

  if (groupKey === "other") {
    return new Set([
      "other",
      "others",
      normalizeSearchValue(labels.other),
    ]).has(normalized);
  }

  return false;
};

const getProfessionalDisplayTitle = (
  person: IPerson,
  listingCategories?: string[] | null
): string => {
  const rawTitle = getProfessionalRawTitle(person);
  if (!rawTitle) {
    return "";
  }

  const groupKey = getProfessionalGroupKey(rawTitle, listingCategories);

  if (
    groupKey === "partners" ||
    groupKey === "associates" ||
    groupKey === "counsel" ||
    groupKey === "other"
  ) {
    return isGenericProfessionalCategoryTitle(
      rawTitle,
      groupKey,
      listingCategories
    )
      ? getProfessionalGroupLabel(groupKey, listingCategories)
      : rawTitle;
  }

  return rawTitle;
};

const sortPersons = (persons: IPerson[]): IPerson[] =>
  [...persons].sort((left, right) => {
    const lastNameCompare = (left.lastName || "").localeCompare(
      right.lastName || ""
    );

    if (lastNameCompare !== 0) {
      return lastNameCompare;
    }

    return (left.name || "").localeCompare(right.name || "");
  });

const matchesFirmBlog = (blog: IBlogCard, firm: IFirm): boolean => {
  const searchableValue = normalizeSearchValue(
    `${blog.author ?? ""} ${blog.title ?? ""}`
  );

  const candidates = [firm.firmName, firm.name]
    .map((value) => normalizeSearchValue(value ?? ""))
    .filter(Boolean);

  return candidates.some((candidate) => searchableValue.includes(candidate));
};

const groupProfessionals = (
  people: IPerson[],
  listingCategories?: string[] | null
): Array<{ key: ProfessionalGroupKey; label: string; people: IPerson[] }> => {
  const groups = people.reduce<Record<ProfessionalGroupKey, IPerson[]>>((accumulator, person) => {
    const groupKey = getProfessionalGroupKey(
      getProfessionalRawTitle(person),
      listingCategories
    );

    if (groupKey === "executive") {
      return accumulator;
    }

    accumulator[groupKey] = accumulator[groupKey] ?? [];
    accumulator[groupKey].push(person);
    return accumulator;
  }, {} as Record<ProfessionalGroupKey, IPerson[]>);

  return Object.entries(groups)
    .map(([key, members]) => ({
      key: key as ProfessionalGroupKey,
      label: getProfessionalGroupLabel(
        key as ProfessionalGroupKey,
        listingCategories
      ),
      people: sortPersons(members),
    }))
    .sort((left, right) => {
      const priorityDelta =
        PROFESSIONAL_GROUP_PRIORITY[left.key] -
        PROFESSIONAL_GROUP_PRIORITY[right.key];

      if (priorityDelta !== 0) {
        return priorityDelta;
      }

      return left.label.localeCompare(right.label);
    });
};

const LinkedTitle: React.FC<{
  className: string;
  target?: LinkTarget;
  children: React.ReactNode;
}> = ({ className, target, children }) => {
  if (!target) {
    return <span className={className}>{children}</span>;
  }

  if (target.external) {
    return (
      <a
        href={target.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={target.href} className={className}>
      {children}
    </Link>
  );
};

const ActionLink: React.FC<{ to: string; label: string; className?: string }> = ({
  to,
  label,
  className = "",
}) => (
  <Link
    to={to}
    className={`inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.06em] text-[#0070CD] hover:text-primary-dark ${className}`.trim()}
  >
    <span>{label}</span>
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#006fcf] p-1 text-white">
      <ChevronRight className="h-3 w-3" />
    </span>
  </Link>
);

const SectionFooterLink: React.FC<{ to: string; label: string }> = ({
  to,
  label,
}) => (
  <Link
    to={to}
    className="group inline-flex items-center gap-3 font-decimal text-[15px] font-semibold uppercase leading-[22.5px] tracking-normal text-[#004578] transition-colors duration-300 hover:text-[#bc3323]"
  >
    <span className="relative pb-px">
      {label}
      <span className="absolute bottom-0 left-0 h-px w-0 bg-[#bc3323] transition-[width] duration-300 ease-out group-hover:w-full" />
    </span>
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#004578] p-1 text-white transition-colors duration-300 group-hover:bg-[#bc3323]">
      <ChevronRight className="h-3.5 w-3.5" />
    </span>
  </Link>
);

const ContentPreviewCard: React.FC<{
  title: string;
  target?: LinkTarget;
  sourceLabel: string;
  dateLabel?: string;
  previewText?: string;
}> = ({ title, target, sourceLabel, dateLabel, previewText }) => (
  <div className="border-b border-[#e8ecef] pb-6 last:border-b-0 last:pb-0">
    <LinkedTitle
      target={target}
      className="block text-[22px] font-semibold leading-[1] text-[#0070CD] hover:underline"
    >
      {title}
    </LinkedTitle>
    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[17px] font-normal leading-[1] text-[#474f56]">
      <span className="text-[#0070CD]">{sourceLabel}</span>
      {dateLabel ? (
        <>
          <span aria-hidden="true" className="text-[#69727d]">
            ,
          </span>
          <span>{dateLabel}</span>
        </>
      ) : null}
    </div>
    {previewText ? (
      <>
        <p
          className="mt-4 text-[17px] font-normal leading-[1.1] text-[#474f56]"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {previewText}
        </p>
        <div className="mt-1 text-center">
          <LinkedTitle
            target={target}
            className="inline-block text-[17px] font-normal leading-[1] text-[#0070CD] hover:underline"
          >
            ...
          </LinkedTitle>
        </div>
      </>
    ) : null}
  </div>
);

const GuideTile: React.FC<{ guide: ContentRow }> = ({ guide }) => {
  const target = resolveGuideTarget(guide);
  const title = guide.title?.trim() || "Guide";

  return (
    <div className="relative overflow-hidden border border-[#e2e5e8] bg-white">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <img
          src={watermark}
          alt=""
          className="h-[9.5rem] w-[9.5rem] object-contain"
        />
      </div>
      <div className="relative z-10 flex min-h-[11.5rem] items-center justify-center px-8 py-10 text-center">
        <LinkedTitle
          target={target}
          className="max-w-[14rem] text-center text-[1.05rem] font-semibold leading-[1.4] text-[#0070CD] underline decoration-1 underline-offset-2 hover:text-primary md:text-[1.2rem]"
        >
          {title}
        </LinkedTitle>
      </div>
    </div>
  );
};

const PublicFirmProfileSection: React.FC<{
  sectionId: SectionKey;
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  sectionRef: (node: HTMLElement | null) => void;
  children: React.ReactNode;
}> = ({ sectionId, title, isOpen, onToggle, sectionRef, children }) => (
  <section
    id={sectionId}
    ref={sectionRef}
    className="scroll-mt-28 border-t border-[#e2e5e8]"
  >
    <div className="flex min-h-[2.6rem] items-stretch md:min-h-[3.125rem]">
      <button
        type="button"
        onClick={onToggle}
        className="flex flex-1 items-center bg-primary px-4 py-5 text-left"
      >
        <span className="font-decimal text-[22px] font-medium uppercase leading-[1.2] tracking-[0.06em] text-white md:text-[23px]">
          {title}
        </span>
      </button>
      <button
        type="button"
        onClick={onToggle}
        aria-label={`${isOpen ? "Collapse" : "Expand"} ${title}`}
        className="flex w-12 shrink-0 items-center justify-center bg-disclosure-primary-dark text-white transition-colors hover:bg-primary-dark md:w-[3.125rem]"
      >
        <ChevronDown
          className={`h-5 w-5 transition-transform ${isOpen ? "" : "-rotate-90"}`}
        />
      </button>
    </div>
    {isOpen ? <div className="bg-white px-4 py-5 md:px-6">{children}</div> : null}
  </section>
);

const PublicPersonCard: React.FC<{
  person: IPerson;
  listingCategories?: string[] | null;
  showEmailAction?: boolean;
}> = ({ person, listingCategories, showEmailAction = true }) => {
  const personPath = buildPersonPath(person);
  const personTitle = getProfessionalDisplayTitle(person, listingCategories);
  const photoSrc = person.id
    ? resolveProfilePictureDisplayUrl(person.id, person.photo, {
        photoUpdatedAt: person._ts,
      })
    : "";

  return (
    <div className="flex items-start gap-3">
      <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100 md:h-[90px] md:w-[90px]">
        <ProfilePhoto
          src={photoSrc}
          alt={`${person.name ?? ""} ${person.lastName ?? ""}`.trim()}
        />
      </div>

      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to={personPath}
            className="block break-words text-[18px] leading-[1.2] text-[#0070CD] hover:underline md:text-[20px] md:font-semibold"
          >
            {person.name ?? ""} {person.lastName ?? ""}
          </Link>
          {personTitle ? (
            <p className="mt-2 text-[18px] font-normal leading-[1.1] text-gray-font md:text-[19px]">
              {personTitle}
            </p>
          ) : null}
        </div>
        {showEmailAction && person.firmEmail ? (
          <>
            <span
              aria-hidden="true"
              className="mt-1 h-8 w-px shrink-0 bg-[#9aa3ad]"
            />
            <a
              href={`mailto:${person.firmEmail}`}
              aria-label={`Email ${person.name ?? ""} ${person.lastName ?? ""}`.trim()}
              className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0070CD] text-white transition-opacity hover:opacity-85"
            >
              <span className="h-3 w-3 fill-current">
                <Email />
              </span>
            </a>
          </>
        ) : null}
      </div>
    </div>
  );
};

const PublicFirmProfileV2: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [firm, setFirm] = useState<IFirm | null>(null);
  const [selectedOffice, setSelectedOffice] = useState<OfficeRecord | null>(
    null
  );
  const [offices, setOffices] = useState<OfficeRecord[]>([]);
  const [addressInfo, setAddressInfo] = useState<IAddress | undefined>();
  const [allPersons, setAllPersons] = useState<IPerson[]>([]);
  const [officeContactGroups, setOfficeContactGroups] = useState<OfficeContactGroup[]>([]);
  const [firmAopsFallback, setFirmAopsFallback] = useState<IAreaOfPractice[]>([]);
  const [firmRepresentativeClients, setFirmRepresentativeClients] = useState<RepresentativeClientRecord[]>([]);
  const [mainContacts, setMainContacts] = useState<IPerson[]>([]);
  const [representativeClientNames, setRepresentativeClientNames] = useState<string[]>([]);
  const [areasOfPractice, setAreasOfPractice] = useState<IAreaOfPractice[]>([]);
  const [articles, setArticles] = useState<ContentRow[]>([]);
  const [pressItems, setPressItems] = useState<ContentRow[]>([]);
  const [blogs, setBlogs] = useState<IBlogCard[]>([]);
  const [guides, setGuides] = useState<ContentRow[]>([]);
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN_SECTIONS);
  const sectionRefs = useRef<Partial<Record<SectionKey, HTMLElement | null>>>({});

  const applyOfficeDetails = useCallback(async (
    office: OfficeRecord,
    firmRecord: IFirm,
    people: IPerson[],
    contactGroups: OfficeContactGroup[],
    firmAops: IAreaOfPractice[],
    representativeClients: RepresentativeClientRecord[]
  ) => {
    const officeId = office.id;
    if (!officeId) {
      return;
    }

    const [officeAddress, officeSpecificAops] = await Promise.all([
      office.address ? getAddresById(office.address) : Promise.resolve(undefined),
      getAOPsByOffice(officeId),
    ]);

    const mainContactIds =
      contactGroups
        .find((entry) => entry.office === officeId)
        ?.contacts?.map((entry) => entry.contact) ?? [];

    const resolvedAops =
      Array.isArray(officeSpecificAops) && officeSpecificAops.length > 0
        ? officeSpecificAops
        : Array.isArray(firmAops)
          ? firmAops
          : [];
    const representativeClientsFromService = Array.from(
      new Set(
        representativeClients
          .filter((client) => matchesRepresentativeClientOffice(client, officeId))
          .map((client) => client.name?.trim())
          .filter(Boolean) as string[]
      )
    );
    const representativeClientsFallback = splitTextareaValues(
      firmRecord.representativeClients
    );

    setSelectedOffice(office);
    setAddressInfo(officeAddress);
    setMainContacts(
      people.filter((person) => Boolean(person.id && mainContactIds.includes(person.id)))
    );
    setRepresentativeClientNames(
      representativeClientsFromService.length > 0
        ? representativeClientsFromService
        : representativeClientsFallback
    );
    setAreasOfPractice(
      [...resolvedAops].sort((left, right) =>
        resolveAopDisplayName(left).localeCompare(resolveAopDisplayName(right))
      )
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!id?.trim()) {
        setError("Missing firm office identifier.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");
      setOpenSections(DEFAULT_OPEN_SECTIONS);

      try {
        let resolvedFirm: IFirm | undefined = await getFirmByShortId(id);

        if (!resolvedFirm) {
          const legacyOffice = (await getOfficeByIdOrLegacyId(id)) as
            | OfficeRecord
            | undefined;

          if (legacyOffice?.firm) {
            resolvedFirm = await getFirmById(legacyOffice.firm);
          }
        }

        if (!resolvedFirm) {
          resolvedFirm = await getFirmById(id);
        }

        if (!resolvedFirm?.id) {
          throw new Error("Firm not found");
        }

        const firmOfficesRaw = await getOfficesByFirmId(resolvedFirm.id);
        const sortedOffices = sortOffices(firmOfficesRaw ?? []);

        const defaultOffice =
          sortedOffices.find((office) => isMainOffice(office)) ||
          sortedOffices[0];

        if (!defaultOffice?.id) {
          throw new Error("Office not found");
        }

        const firmPublicId = resolveFirmPublicId(resolvedFirm);
        const requestedPublicId = id.trim();

        const [
          peopleRaw,
          officeContacts,
          firmAops,
          firmRepresentativeClients,
          latestArticles,
          latestPress,
          latestGuides,
          allBlogs,
        ] = await Promise.all([
          fetchPersonByFirm(resolvedFirm.id),
          getOfficeContactByFirmId(resolvedFirm.id),
          getAOPsByFirm(resolvedFirm.id),
          getRepresentativeClientsByFirm(resolvedFirm.id),
          getLastContents(2, "article", resolvedFirm.id),
          getLastContents(2, "press", resolvedFirm.id),
          getLastContents(2, "guide", resolvedFirm.id),
          getBlogs(),
        ]);

        if (cancelled) {
          return;
        }

        const normalizedPeople = sortPersons((peopleRaw ?? []) as IPerson[]);
        const contactGroups = (officeContacts?.offices ?? []) as OfficeContactGroup[];
        const firmAopsList = Array.isArray(firmAops)
          ? (firmAops as IAreaOfPractice[])
          : [];
        const firmRepresentativeClientList = (
          firmRepresentativeClients ?? []
        ) as RepresentativeClientRecord[];

        const filteredBlogs = (allBlogs ?? [])
          .filter((blog) => !blog.isDeleted && matchesFirmBlog(blog, resolvedFirm as IFirm))
          .sort(
            (left, right) =>
              new Date(right.dateCreated).getTime() -
              new Date(left.dateCreated).getTime()
          )
          .slice(0, 2);

        setFirm(resolvedFirm);
        setOffices(sortedOffices);
        setAllPersons(normalizedPeople);
        setOfficeContactGroups(contactGroups);
        setFirmAopsFallback(firmAopsList);
        setFirmRepresentativeClients(firmRepresentativeClientList);
        setArticles((latestArticles ?? []) as ContentRow[]);
        setPressItems((latestPress ?? []) as ContentRow[]);
        setGuides((latestGuides ?? []) as ContentRow[]);
        setBlogs(filteredBlogs);
        await applyOfficeDetails(
          defaultOffice,
          resolvedFirm,
          normalizedPeople,
          contactGroups,
          firmAopsList,
          firmRepresentativeClientList
        );

        if (isUuidValue(requestedPublicId) && requestedPublicId !== firmPublicId) {
          navigate(buildFirmProfilePath(resolvedFirm), { replace: true });
        }
      } catch (loadError) {
        console.error(loadError);

        if (cancelled) {
          return;
        }

        setFirm(null);
        setSelectedOffice(null);
        setOffices([]);
        setAddressInfo(undefined);
        setAllPersons([]);
        setOfficeContactGroups([]);
        setFirmAopsFallback([]);
        setFirmRepresentativeClients([]);
        setMainContacts([]);
        setRepresentativeClientNames([]);
        setAreasOfPractice([]);
        setArticles([]);
        setPressItems([]);
        setBlogs([]);
        setGuides([]);
        setError("We couldn't load this firm profile.");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [applyOfficeDetails, id, navigate]);

  const firmDisplayName = firm?.firmName || firm?.name || "Firm Profile";
  const officeProfessionals = useMemo(
    () =>
      sortPersons(
        allPersons.filter((person) => matchesOffice(person, selectedOffice?.id))
      ),
    [allPersons, selectedOffice?.id]
  );
  const professionalGroups = useMemo(
    () => groupProfessionals(officeProfessionals, firm?.listingCategory),
    [officeProfessionals, firm?.listingCategory]
  );
  const overviewText = (
    firm?.firmOverview ||
    firm?.overview ||
    selectedOffice?.overview ||
    ""
  ).trim();
  const notables = useMemo(
    () =>
      (firm?.notables ?? []).filter((notable) =>
        Boolean(
          typeof notable.photo === "string" && notable.photo.trim()
        )
      ),
    [firm?.notables]
  );
  const displayArticles = useMemo(
    () =>
      articles.filter((article) =>
        Boolean(
          article.title?.trim() ||
            extractPlainText(
              article.articleContent || article.footnotes || article.link
            )
        )
      ),
    [articles]
  );
  const displayPressItems = useMemo(
    () =>
      pressItems.filter((press) =>
        Boolean(
          press.title?.trim() ||
            extractPlainText(press.articleContent || press.footnotes || press.link)
        )
      ),
    [pressItems]
  );
  const displayBlogs = useMemo(
    () =>
      blogs.filter((blog) =>
        Boolean(blog.title?.trim() || extractPlainText(blog.postContent))
      ),
    [blogs]
  );
  const displayGuides = useMemo(
    () => guides.filter((guide) => Boolean(guide.title?.trim())),
    [guides]
  );
  const websiteUrl = withProtocol(firm?.websiteUrl);
  const websiteLabel = firm?.websiteUrl?.trim().replace(/^https?:\/\//i, "") || "";
  const officePhone = formatPhoneForDisplay(selectedOffice?.phoneNumber);
  const officeFax = formatPhoneForDisplay(selectedOffice?.faxNumber);
  const officeTitleLocation = selectedOffice
    ? formatLocationText(selectedOffice)
    : "";
  const serviceAndLocationParts = [firm?.serviceType, officeTitleLocation].filter(
    Boolean
  ) as string[];
  const officeStreet = [
    addressInfo?.address1?.trim(),
    addressInfo?.address2?.trim(),
  ]
    .filter(Boolean)
    .join(" ");
  const officeStreetLine = officeStreet || selectedOffice?.addressName?.trim() || "";
  const hasMainContacts = mainContacts.length > 0;
  const hasMultipleOffices = offices.length > 1;
  const useSplitTopMeta = hasMainContacts && hasMultipleOffices;
  const displayedOffices = useMemo(() => {
    if (!selectedOffice?.id) {
      return offices;
    }

    const selectedIndex = offices.findIndex(
      (office) => office.id === selectedOffice.id
    );

    if (selectedIndex <= 0) {
      return offices;
    }

    return [
      offices[selectedIndex],
      ...offices.slice(0, selectedIndex),
      ...offices.slice(selectedIndex + 1),
    ];
  }, [offices, selectedOffice?.id]);
  const sectionVisibility = useMemo<Record<SectionKey, boolean>>(
    () => ({
      profile: Boolean(
        overviewText || selectedOffice?.yearFounded || notables.length > 0
      ),
      aop: areasOfPractice.length > 0,
      clients: representativeClientNames.length > 0,
      professionals: professionalGroups.length > 0,
      articles: displayArticles.length > 0,
      press: displayPressItems.length > 0,
      blogs: displayBlogs.length > 0,
      guides: displayGuides.length > 0,
    }),
    [
      overviewText,
      selectedOffice?.yearFounded,
      notables.length,
      areasOfPractice.length,
      representativeClientNames.length,
      professionalGroups.length,
      displayArticles.length,
      displayPressItems.length,
      displayBlogs.length,
      displayGuides.length,
    ]
  );
  const visibleSectionNavItems = useMemo(
    () => SECTION_NAV_ITEMS.filter((item) => sectionVisibility[item.key]),
    [sectionVisibility]
  );

  const handleSectionToggle = (key: SectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleOfficeChange = async (office: OfficeRecord) => {
    if (!firm || office.id === selectedOffice?.id) {
      return;
    }

    await applyOfficeDetails(
      office,
      firm,
      allPersons,
      officeContactGroups,
      firmAopsFallback,
      firmRepresentativeClients
    );
  };

  const navigateToSection = (key: SectionKey) => {
    if (!sectionVisibility[key]) {
      return;
    }

    setOpenSections((current) => ({
      ...current,
      [key]: true,
    }));

    window.setTimeout(() => {
      sectionRefs.current[key]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, SECTION_SCROLL_DELAY_MS);
  };

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[72rem] px-4 py-8">
        <div className="border border-[#e2e5e8] bg-white px-6 py-10 text-center text-gray-font">
          Loading firm profile...
        </div>
      </div>
    );
  }

  if (error || !firm || !selectedOffice) {
    return (
      <div className="mx-auto w-full max-w-[72rem] px-4 py-8">
        <div className="border border-[#e2e5e8] bg-white px-6 py-10 text-center text-gray-font">
          {error || "Firm profile not found."}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[120rem] px-4 py-8">
      <div className="overflow-hidden border border-[#dfe3e8] bg-white shadow-sm">
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur">
          <div className="mx-4 border-y border-[#dfe3e8] py-6 md:mx-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              {visibleSectionNavItems.length > 0 ? (
                <nav className="flex flex-wrap items-center text-[1.05rem] font-semibold text-[#4d5761]">
                  {visibleSectionNavItems.map((item, index) => (
                    <React.Fragment key={item.key}>
                      <button
                        type="button"
                        onClick={() => navigateToSection(item.key)}
                        className="text-left transition-colors hover:text-primary"
                      >
                        {item.label}
                      </button>
                      {index < visibleSectionNavItems.length - 1 ? (
                        <span
                          aria-hidden="true"
                          className="px-4 text-[#69727d]"
                        >
                          |
                        </span>
                      ) : null}
                    </React.Fragment>
                  ))}
                </nav>
              ) : null}

              <ActionLink
                to="/newsfeeds"
                label={`Latest News ${selectedOffice.countryName || "WSG"}`}
                className="shrink-0 self-start lg:self-auto"
              />
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden px-4 py-8 md:px-6 md:py-10 lg:px-8 lg:py-12">
          <div className="pointer-events-none absolute inset-x-0 top-[3.5rem] flex justify-center md:hidden">
            <img
              src={watermark}
              alt=""
              className="h-[18rem] w-auto object-contain"
            />
          </div>
          <div className="pointer-events-none absolute right-[-7rem] top-[2rem] hidden h-[22rem] w-[24rem] md:block lg:right-[-6rem] lg:top-[2rem] lg:h-[30rem] lg:w-[32rem]">
            <img
              src={watermark}
              alt=""
              className="object-contain object-right-top h-[20rem]"
            />
          </div>

          <div className="relative z-10 grid gap-6 lg:grid-cols-2 lg:items-center lg:gap-12">
            <div className="order-2 min-w-0 max-w-[38rem] lg:order-1">
              <h1 className="font-decimal text-[2rem] font-semibold leading-[0.94] tracking-[-0.02em] text-primary md:text-[24px] md:font-medium md:leading-[1] md:tracking-[0]">
                {firmDisplayName}
              </h1>
              {serviceAndLocationParts.length > 0 ? (
                <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[1.05rem] font-normal leading-[1.3] tracking-[0] text-gray-font md:text-[21px] md:leading-[1]">
                  {serviceAndLocationParts.map((part, index) => (
                    <React.Fragment key={`${part}-${index}`}>
                      <span>{part}</span>
                      {index < serviceAndLocationParts.length - 1 ? (
                        <span aria-hidden="true" className="text-[#9aa3ad]">
                          |
                        </span>
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              ) : null}

              <div className="mt-4 md:mt-5">
                <SocialLinks
                  organization
                  organizationObj={firm}
                  rowClassName="justify-start gap-3"
                />
              </div>

              {websiteUrl ? (
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-block break-all text-[1.1rem] font-semibold leading-[1.35] tracking-[0] text-[#006FCF] underline decoration-1 underline-offset-2 hover:text-primary-dark md:text-[21px] md:font-normal md:leading-[1]"
                >
                  {websiteLabel || websiteUrl}
                </a>
              ) : null}

              <div className="mt-6 space-y-3 text-[1.05rem] font-normal leading-[1.55] tracking-[0] text-gray-font md:space-y-2 md:text-[21px] md:leading-[1]">
                {(officePhone || officeFax) ? (
                  <div className="flex flex-col gap-1 md:block">
                    {officePhone ? (
                      <p className="break-words">{`tel: ${officePhone}`}</p>
                    ) : null}
                    {officeFax ? (
                      <p className="break-words">{`fax: ${officeFax}`}</p>
                    ) : null}
                  </div>
                ) : null}
                {officeStreetLine ? (
                  <p className="break-words">{officeStreetLine}</p>
                ) : null}
              </div>
            </div>

            <div className="relative z-10 order-1 flex min-h-[5rem] items-center justify-center lg:order-2 lg:min-h-[14rem] lg:justify-start">
              {firm.logo ? (
                <img
                  src={firm.logo}
                  alt={`${firmDisplayName} logo`}
                  className="max-h-[6.5rem] max-w-[16rem] object-contain md:max-h-[8rem] md:max-w-[18rem] lg:max-h-[19rem] lg:max-w-[19rem]"
                />
              ) : null}
            </div>
          </div>

          {hasMainContacts || hasMultipleOffices ? (
            <div className="relative z-10 mt-8 border-t border-[#dfe3e8] pt-5">
              <div
                className={`grid gap-8 ${useSplitTopMeta ? "lg:grid-cols-2 lg:gap-10" : ""}`}
              >
                {hasMainContacts ? (
                  <div className="min-w-0">
                    <h2 className="text-[20px] text-gray-font">
                      Main Contacts
                    </h2>
                    <div
                      className={`mt-6 grid gap-x-6 gap-y-6 ${
                        useSplitTopMeta ? "xl:grid-cols-2" : "lg:grid-cols-2"
                      }`}
                    >
                      {mainContacts.map((person) => (
                        <PublicPersonCard
                          key={person.id}
                          person={person}
                          listingCategories={firm?.listingCategory}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {hasMultipleOffices ? (
                  <div className="min-w-0">
                    <h2 className="text-[20px] text-gray-font">
                      Offices
                    </h2>
                    <div className="mt-6 text-[20px] font-normal leading-[1.55] text-primary">
                      {displayedOffices.map((office, index) => {
                        const isActive = office.id === selectedOffice.id;

                        return (
                          <React.Fragment key={office.id}>
                            {isActive ? (
                              <span className="font-semibold text-gray-font">
                                {formatOfficeTabLabel(office)}
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  void handleOfficeChange(office);
                                }}
                                className="text-[#0070CD] transition-colors hover:underline"
                              >
                                {formatOfficeTabLabel(office)}
                              </button>
                            )}
                            {index < displayedOffices.length - 1 ? (
                              <span className="px-2 text-[#69727d]">|</span>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {sectionVisibility.profile ? (
          <PublicFirmProfileSection
            sectionId="profile"
            title="Overview"
            isOpen={openSections.profile}
            onToggle={() => handleSectionToggle("profile")}
            sectionRef={(node) => {
              sectionRefs.current.profile = node;
            }}
          >
            <div className="space-y-5 text-sm leading-7 text-gray-font md:text-base">
              {overviewText ? (
                <p className="whitespace-pre-line">{overviewText}</p>
              ) : null}
              {selectedOffice.yearFounded ? (
                <p className="font-semibold text-[#474f56]">
                  Year this Office was Established: {selectedOffice.yearFounded}
                </p>
              ) : null}
              {notables.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-[20px] text-[#474f56]">
                    Notable
                  </p>
                  <div className="flex flex-wrap items-start gap-6">
                    {notables.map((notable, index) => {
                      const notableUrl = withProtocol(notable.link);
                      const notablePhoto =
                        typeof notable.photo === "string" ? notable.photo : "";

                      return (
                        <a
                          key={`${notable.link}-${index}`}
                          href={notableUrl || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex max-w-[13rem] items-center justify-start ${
                            notableUrl ? "transition-opacity hover:opacity-80" : ""
                          }`}
                        >
                          <img
                            src={notablePhoto}
                            alt="Notable"
                            className="max-h-[5.5rem] max-w-full object-contain md:max-h-[6.5rem]"
                          />
                        </a>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </PublicFirmProfileSection>
        ) : null}

        {sectionVisibility.aop ? (
          <PublicFirmProfileSection
            sectionId="aop"
            title="Areas of Practice"
            isOpen={openSections.aop}
            onToggle={() => handleSectionToggle("aop")}
            sectionRef={(node) => {
              sectionRefs.current.aop = node;
            }}
          >
            <div className="grid gap-x-8 gap-y-2 md:grid-cols-2 xl:grid-cols-3">
              {areasOfPractice.map((aop) => (
                <div key={aop.id} className="text-sm leading-7 text-gray-font md:text-base">
                  &bull; {resolveAopDisplayName(aop)}
                </div>
              ))}
            </div>
          </PublicFirmProfileSection>
        ) : null}

        {sectionVisibility.clients ? (
          <PublicFirmProfileSection
            sectionId="clients"
            title="Representative Clients"
            isOpen={openSections.clients}
            onToggle={() => handleSectionToggle("clients")}
            sectionRef={(node) => {
              sectionRefs.current.clients = node;
            }}
          >
            <div className="grid gap-x-8 gap-y-2 md:grid-cols-2 xl:grid-cols-3">
              {representativeClientNames.map((clientName) => (
                <div key={clientName} className="text-sm leading-7 text-gray-font md:text-base">
                  &bull; {clientName}
                </div>
              ))}
            </div>
          </PublicFirmProfileSection>
        ) : null}

        {sectionVisibility.professionals ? (
          <PublicFirmProfileSection
            sectionId="professionals"
            title="Professionals"
            isOpen={openSections.professionals}
            onToggle={() => handleSectionToggle("professionals")}
            sectionRef={(node) => {
              sectionRefs.current.professionals = node;
            }}
          >
            <div className="space-y-8">
              {professionalGroups.map((group) => (
                <div key={group.key}>
                  <div className="flex items-center gap-3">
                    <h3 className="font-decimal text-base font-medium text-[#474f56] md:text-lg">
                      {group.label}
                    </h3>
                    <div className="h-px flex-1 bg-[#dfe3e8]" />
                  </div>
                  <div className="mt-4 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {group.people.map((person) => (
                      <PublicPersonCard
                        key={person.id}
                        person={person}
                        listingCategories={firm?.listingCategory}
                        showEmailAction={false}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </PublicFirmProfileSection>
        ) : null}

        {sectionVisibility.articles ? (
          <PublicFirmProfileSection
            sectionId="articles"
            title="Articles"
            isOpen={openSections.articles}
            onToggle={() => handleSectionToggle("articles")}
            sectionRef={(node) => {
              sectionRefs.current.articles = node;
            }}
          >
            <div className="space-y-6">
              {displayArticles.map((article) => {
                const target = resolveContentTarget(article, "article");
                const previewText = truncateText(
                  extractPlainText(
                    article.articleContent || article.footnotes || article.link
                  ),
                  320
                );

                return (
                  <ContentPreviewCard
                    key={article.id}
                    title={article.title}
                    target={target}
                    sourceLabel={firmDisplayName}
                    dateLabel={
                      article.publishedDate
                        ? formatMonthYear(article.publishedDate)
                        : undefined
                    }
                    previewText={previewText}
                  />
                );
              })}
              <SectionFooterLink
                to="/publications.asp"
                label={`View All ${firmDisplayName} Articles`}
              />
            </div>
          </PublicFirmProfileSection>
        ) : null}

        {sectionVisibility.press ? (
          <PublicFirmProfileSection
            sectionId="press"
            title="Press"
            isOpen={openSections.press}
            onToggle={() => handleSectionToggle("press")}
            sectionRef={(node) => {
              sectionRefs.current.press = node;
            }}
          >
            <div className="space-y-6">
              {displayPressItems.map((press) => {
                const target = resolveContentTarget(press, "press");
                const previewText = truncateText(
                  extractPlainText(press.articleContent || press.footnotes || press.link),
                  320
                );

                return (
                  <ContentPreviewCard
                    key={press.id}
                    title={press.title}
                    target={target}
                    sourceLabel={firmDisplayName}
                    dateLabel={
                      press.publishedDate
                        ? formatMonthYear(press.publishedDate)
                        : undefined
                    }
                    previewText={previewText}
                  />
                );
              })}
              <SectionFooterLink
                to="/releases.asp"
                label={`View All ${firmDisplayName} Press`}
              />
            </div>
          </PublicFirmProfileSection>
        ) : null}

        {sectionVisibility.blogs ? (
          <PublicFirmProfileSection
            sectionId="blogs"
            title="Blogs"
            isOpen={openSections.blogs}
            onToggle={() => handleSectionToggle("blogs")}
            sectionRef={(node) => {
              sectionRefs.current.blogs = node;
            }}
          >
            <div className="space-y-6">
              {displayBlogs.map((blog) => {
                const target = resolveBlogTarget(blog);
                const previewText = truncateText(
                  extractPlainText(blog.postContent),
                  320
                );

                return (
                  <ContentPreviewCard
                    key={blog.id ?? blog.slug}
                    title={blog.title}
                    target={target}
                    sourceLabel={blog.author || firmDisplayName}
                    dateLabel={
                      blog.dateCreated
                        ? formatMonthYear(blog.dateCreated)
                        : undefined
                    }
                    previewText={previewText}
                  />
                );
              })}
              <SectionFooterLink
                to="/blog/default.aspx"
                label={`View All ${firmDisplayName} Blogs`}
              />
            </div>
          </PublicFirmProfileSection>
        ) : null}

        {sectionVisibility.guides ? (
          <PublicFirmProfileSection
            sectionId="guides"
            title="Guides"
            isOpen={openSections.guides}
            onToggle={() => handleSectionToggle("guides")}
            sectionRef={(node) => {
              sectionRefs.current.guides = node;
            }}
          >
            <div className="grid gap-6 md:grid-cols-2 xl:max-w-[52rem]">
              {displayGuides.map((guide) => (
                <GuideTile
                  key={guide.id ?? guide.shortId ?? guide.title}
                  guide={guide}
                />
              ))}
            </div>
          </PublicFirmProfileSection>
        ) : null}
      </div>
    </div>
  );
};

export default PublicFirmProfileV2;
